/**
 * Capacitor Setup Component
 *
 * Handles native platform initialization including:
 * - Deep link handling (appUrlOpen)
 * - NFC background listeners (Android)
 * - Status bar styling
 * - Edge-to-edge display (Android)
 * - NFC availability checking
 *
 * IMPORTANT: This component sets up listeners that should persist for the
 * entire app lifecycle. We use refs to avoid recreating listeners on navigation.
 */

import SettingsIcon from '@mui/icons-material/Settings';
import { useColorScheme, useTheme } from '@mui/material/styles';

import { lookupEquipment } from 'src/api/equipment.api';
import { useAuthContext } from 'src/auth/hooks';
import { usePersistentMessages } from 'src/components/persistent-messages';
import { enqueueSnackbar } from 'src/components/snackbar';
import { useRecordScanLocation } from 'src/hooks/use-scan-location';
import { useTranslate } from 'src/locales/use-locales';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import {
  scannedEquipmentIds,
  searchAndNavigateToEquipment,
  shareableIdFromShortlinkUrl,
} from 'src/utils/equipment-search';
import { nfcSetupLogger as log } from './nfc-logger';
import { Nfc } from './nfc-plugin';

import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { useCallback, useEffect, useRef } from 'react';

// ============================================================================
// Constants
// ============================================================================

const NFC_DISABLED_MESSAGE_ID = 'nfc-disabled';
const NFC_STATUS_CHECK_DELAY_MS = 300;

// ============================================================================
// Component
// ============================================================================

export const CapacitorSetup = () => {
  const { mode } = useColorScheme();
  const theme = useTheme();
  const router = useRouter();
  const { addMessage, removeMessage } = usePersistentMessages();
  const { t: tPersistentMessages } = useTranslate(
    'components.persistentMessages',
  );
  const { t: tMobileSpecific } = useTranslate('mobile-specific');
  const { isAuthenticated } = useAuthContext();

  // Track initialization to prevent double setup
  const initRef = useRef(false);
  const listenersSetupRef = useRef(false);

  // Use refs to avoid recreating listeners on every render/navigation
  // These refs are updated on every render but the listeners only read the current value
  const routerRef = useRef(router);
  const tMobileSpecificRef = useRef(tMobileSpecific);
  // Behind a ref like the rest: this callback changes when the viewport crosses
  // the `sm` breakpoint, which must not tear down the NFC listeners.
  const recordScanLocation = useRecordScanLocation();
  const recordScanLocationRef = useRef(recordScanLocation);

  // Keep refs up to date
  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  useEffect(() => {
    recordScanLocationRef.current = recordScanLocation;
  }, [recordScanLocation]);

  useEffect(() => {
    tMobileSpecificRef.current = tMobileSpecific;
  }, [tMobileSpecific]);

  // ============================================================================
  // URL Redirect Handler (uses ref to avoid dependency issues)
  // ============================================================================

  const redirectFromUrl = useCallback((url: string) => {
    log.info('Redirecting from URL', { url });

    try {
      const urlObj = new URL(url);
      const target = `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;

      if (urlObj.pathname) {
        log.debug('Navigating to target', { target });
        routerRef.current.replace(target);
      }
    } catch (error) {
      log.error('Invalid URL format', { url, error });
    }
  }, []); // No dependencies - uses ref

  // ============================================================================
  // Deep Link Handler (iOS + Android) - Setup once on mount
  // ============================================================================

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    log.info('Setting up deep link listener');

    const appUrlOpenListener = App.addListener(
      'appUrlOpen',
      (event: URLOpenListenerEvent) => {
        log.info('Deep link received', { url: event.url });
        redirectFromUrl(event.url);
      },
    );

    return () => {
      appUrlOpenListener.then((handle) => handle.remove());
    };
  }, [redirectFromUrl]);

  // ============================================================================
  // NFC Background Listeners (Android only) - Setup once on mount
  // ============================================================================

  useEffect(() => {
    if (
      !Capacitor.isNativePlatform() ||
      Capacitor.getPlatform() !== 'android'
    ) {
      return;
    }

    // Prevent double setup (React StrictMode or re-renders)
    if (listenersSetupRef.current) {
      log.debug('NFC listeners already setup, skipping');
      return;
    }
    listenersSetupRef.current = true;

    log.info('Setting up NFC background listeners (Android)');

    // GearTracker tag detected - redirect to URL
    const nfcUrlListener = Nfc.on('gearTrackerTag', async (event) => {
      if (!('url' in event) || typeof event.url !== 'string') {
        log.warn('GearTracker tag without URL', event);
        return;
      }

      log.info('GearTracker tag detected (background)', { url: event.url });
      redirectFromUrl(event.url);

      // The tag was physically tapped, so the equipment is here. This is the
      // only way to know that: opening the very same shortlink from a mail or
      // a chat message reaches the same page without the equipment being
      // anywhere near — hence the resolver itself stays out of this.
      const shareableId = shareableIdFromShortlinkUrl(event.url);
      if (!shareableId || !isAuthenticated) {
        return;
      }

      const lookupResult = await lookupEquipment(shareableId).catch(() => ({
        type: 'none' as const,
      }));
      void recordScanLocationRef.current(scannedEquipmentIds(lookupResult));
    });

    // Other tag detected - search for equipment by serial number
    const otherTagListener = Nfc.on('otherTag', async (event) => {
      const serialNumber =
        'serialNumber' in event ? event.serialNumber : undefined;
      log.info('Other NFC tag detected (background)', {
        serialNumber,
      });

      if (!serialNumber) {
        log.warn('No serial number in tag event');
        return;
      }

      const lookupResult = isAuthenticated
        ? await lookupEquipment(serialNumber).catch(() => ({
            type: 'none' as const,
          }))
        : { type: 'none' as const };

      // Same rule as the in-app scanner: a tag shared by several equipments
      // locates all of them, not only the unambiguous match.
      void recordScanLocationRef.current(scannedEquipmentIds(lookupResult));

      const result = searchAndNavigateToEquipment(
        serialNumber,
        lookupResult,
        routerRef.current,
        { onNoResult: 'new-equipment' },
      );

      log.info('Equipment search result', {
        searchTerm: serialNumber,
        resultType: result.type,
      });

      // Show snackbar for unknown tag
      if (result.type === 'none') {
        enqueueSnackbar(tMobileSpecificRef.current('nfc.unknownTagDetected'), {
          variant: 'info',
        });
      }
    });

    // Empty tag detected - redirect to equipment creation
    const emptyTagListener = Nfc.on('emptyTag', (event) => {
      log.info('Empty NFC tag detected (background)', {
        serialNumber: event.serialNumber,
      });
      enqueueSnackbar(tMobileSpecificRef.current('nfc.emptyTagDetected'), {
        variant: 'info',
      });
      routerRef.current.replace(paths.equipment.new());
    });

    // Signal that app is ready to receive pending intents
    log.debug('Signaling app ready for pending intents');
    Nfc.setAppAsReady();

    // Enable background NFC listening so tags are detected from any view
    Nfc.enableBackgroundListening();

    return () => {
      log.debug('Cleaning up NFC listeners');
      listenersSetupRef.current = false;
      nfcUrlListener.then((handle) => handle.remove());
      otherTagListener.then((handle) => handle.remove());
      emptyTagListener.then((handle) => handle.remove());
    };
  }, [redirectFromUrl, isAuthenticated]); // Only redirectFromUrl which is stable

  // ============================================================================
  // NFC Status Check (Android only)
  // ============================================================================

  const checkNfcStatus = useCallback(async () => {
    if (
      !Capacitor.isNativePlatform() ||
      Capacitor.getPlatform() !== 'android'
    ) {
      return;
    }

    try {
      const { available, enabled } = await Nfc.nfcStatus();
      log.debug('NFC status checked', { available, enabled });

      if (available && !enabled) {
        log.info('NFC available but disabled, showing message');
        addMessage({
          id: NFC_DISABLED_MESSAGE_ID,
          type: 'info',
          title: tPersistentMessages('nfc-disabled.title'),
          description: tPersistentMessages('nfc-disabled.description'),
          action: {
            label: tPersistentMessages('nfc-disabled.action'),
            icon: <SettingsIcon />,
            onClick: async () => {
              try {
                await Nfc.openNfcSettings();
              } catch (error) {
                log.error('Failed to open NFC settings', error);
              }
            },
          },
        });
      } else {
        // NFC is enabled or not available - remove any existing message
        removeMessage(NFC_DISABLED_MESSAGE_ID);
      }
    } catch (error) {
      log.error('Error checking NFC status', error);
    }
  }, [addMessage, removeMessage, tPersistentMessages]);

  // Check NFC status on mount and when app becomes active
  useEffect(() => {
    if (
      !Capacitor.isNativePlatform() ||
      Capacitor.getPlatform() !== 'android'
    ) {
      return;
    }

    // Initial check
    log.debug('Initial NFC status check');
    void checkNfcStatus();

    // Re-check when app becomes active (user might have enabled NFC in settings)
    let cleanup: (() => void) | undefined;

    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        log.debug(
          'App became active, re-checking NFC status and re-enabling listening',
        );
        // Small delay to ensure NFC state has been updated by the system
        setTimeout(() => {
          void checkNfcStatus();
          // Re-enable background listening after coming back to foreground
          Nfc.enableBackgroundListening();
        }, NFC_STATUS_CHECK_DELAY_MS);
      }
    }).then((listener) => {
      cleanup = () => listener.remove();
    });

    return () => {
      cleanup?.();
    };
  }, [checkNfcStatus]);

  // ============================================================================
  // Edge-to-Edge Setup (Android only)
  // ============================================================================

  useEffect(() => {
    if (
      !Capacitor.isNativePlatform() ||
      Capacitor.getPlatform() !== 'android' ||
      initRef.current
    ) {
      return;
    }

    log.debug('Enabling edge-to-edge display');
    void EdgeToEdge.enable();
    initRef.current = true;
  }, []);

  // ============================================================================
  // Theme-based Styling
  // ============================================================================

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const platform = Capacitor.getPlatform();
    const backgroundColor = theme.palette.background.default;

    // Set background color
    if (platform === 'android') {
      void EdgeToEdge.setBackgroundColor({ color: backgroundColor });
    } else if (platform === 'ios') {
      void StatusBar.setBackgroundColor({ color: backgroundColor }).catch(
        () => {
          // setBackgroundColor may not be fully supported on iOS
        },
      );
    }

    // Set status bar style based on theme mode
    void StatusBar.setStyle({
      style: mode === 'light' ? Style.Light : Style.Dark,
    });
  }, [mode, theme.palette.background.default]);

  return null;
};
